import React from 'react';
import ConsentBubble from './ConsentBubble';
import { ChatMarkdownContent } from './ChatMarkdownContent';
import styles from '../ChatWidget.module.scss';
import { LlmConsent, LlmTokenUsage, useDateHelper, useLlmTokenUsageService, useLlmConsentService, LLM_CONSENT_UPDATED } from 'nekst-api';
import nekstAiIcon from './nekstAiIcon.png';
import { useDataLoaderNew } from 'data-loader';

const WELCOME_TEXT = `Hi! I'm **Nekst AI**, your real estate transaction assistant. Here's how I can help:

- **Transactions** — look up details, tasks, notes, and history
- **Contacts & people** — search contacts, view team members and their activity
- **Tasks** — check assignments, due dates, and overdue key dates

Just type your question below to get started!`;

function isFreePlan(planId: string | null): boolean {
  return !planId || planId === 'FREE_MONTHLY';
}

export function UsageLimitWarning(props: { usage: LlmTokenUsage }) {
  const { usage } = props;
  const dateHelper = useDateHelper();

  if (usage.percentUsed >= 100) {
    if (isFreePlan(usage.planId)) {
      return (
        <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
          <div className={styles.bubbleAvatarSpacer} />
          <div className={styles.bubbleWrapper}>
            <div className={styles.limitReachedBubble}>
              Your team has used its included AI usage. Consider upgrading your plan for more AI capacity.
            </div>
          </div>
        </div>
      );
    } else {
      const resetDate = dateHelper.toString(usage.periodEnd);
      return (
        <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
          <div className={styles.bubbleAvatarSpacer} />
          <div className={styles.bubbleWrapper}>
            <div className={styles.limitReachedBubble}>
              Your team has used its included AI usage. Resets on {resetDate}. Contact support for additional capacity.
            </div>
          </div>
        </div>
      );
    }
  } else if (usage.percentUsed >= 80) {
    return (
      <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
        <div className={styles.bubbleAvatarSpacer} />
        <div className={styles.bubbleWrapper}>
          <div className={styles.warningBubble}>
            Your team is approaching its included AI usage for this month.
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
}

export function WelcomeMessage() {
  return (
    <div className={`${styles.messageRow} ${styles.messageRowAssistant}`}>
      <div className={styles.bubbleAvatar}>
        <img src={nekstAiIcon} alt="Nekst AI" width={28} height={28} />
      </div>
      <div className={styles.bubbleWrapper}>
        <div className={styles.bubbleMeta}>
          <span className={styles.bubbleSender}>Nekst AI</span>
        </div>
        <div className={styles.assistantBubble}>
          <ChatMarkdownContent content={WELCOME_TEXT} />
        </div>
      </div>
    </div>
  );
}

export interface InitialBubbleContentProps {
  consent: LlmConsent | null;
  usage: LlmTokenUsage | null;
}

export function InitialBubbleContent(props: InitialBubbleContentProps) {
  const { consent, usage } = props;

  if (consent === null) {
    return null;
  }

  if (!consent.optedIn) {
    return <ConsentBubble />;
  }

  return (
    <>
      <WelcomeMessage />
      {usage && <UsageLimitWarning usage={usage} />}
    </>
  );
}

export default function InitialBubble() {
  const usageService = useLlmTokenUsageService();
  const { data: usageData } = useDataLoaderNew(usageService.getUsage);

  const consentService = useLlmConsentService();
  const { data: consentData } = useDataLoaderNew(
    consentService.getConsent,
    { events: [LLM_CONSENT_UPDATED] },
  );

  return <InitialBubbleContent consent={consentData} usage={usageData} />;
}
